import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { NarrowSidebar } from './NarrowSidebar';
import { TopNav } from './TopNav';
import { Lightbar } from './Lightbar';
import { BottomBar } from './BottomBar';
import { TopoBackground } from './TopoBackground';
import { PendingApprovalView } from './PendingApprovalView';
import { MobileHeader, NavBackdrop } from './MobileChrome';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../auth/AuthProvider';
import { useAppCatalog } from '../hooks/useAppCatalog';
import { useViewport, useMobileNav } from '../hooks/useViewport';
import { APP_CATEGORIES } from '../data/app-categories';
import { COLORS } from '../styles/tokens';
import type { AppFilters } from '../hooks/useAppFilter';

/** Context passed to child routes via Outlet */
export interface LayoutContext {
  activeTab: string;
  searchQuery: string;
  filters: AppFilters;
}

/** Shell layout: NarrowSidebar | [TopNav / Lightbar / Content / BottomBar] */
export function Layout(): React.JSX.Element {
  const { isDark } = useTheme();
  const { isPending } = useAuth();
  const { isMobile } = useViewport();
  const bg = isDark ? COLORS.dark.bg : COLORS.light.bg;

  const location = useLocation();
  const { menuOpen, toggleMenu, closeMenu } = useMobileNav(location.pathname);
  const isAdminPage = location.pathname.startsWith('/admin');
  /** Only the app-catalog pages need the category tab bar + bottom filters. */
  const showCatalogChrome = location.pathname === '/';

  const { apps } = useAppCatalog();
  /** Show only curated categories that actually have a visible app, so empty
   *  categories don't render as dead tabs. Curated order is preserved. */
  const tabs = ['ALL APPS', ...APP_CATEGORIES.filter(cat => apps.some(app => app.category === cat))];

  const [activeTab, setActiveTab] = useState('ALL APPS');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<AppFilters>({ statuses: [], teams: [] });

  /** All unique teams from the app catalog */
  const allTeams = [...new Set(apps.map(a => a.team).filter(Boolean) as string[])];

  return (
    <div
      className="vx-shell"
      style={{ display: 'flex', height: '100vh', backgroundColor: bg, transition: 'background-color 0.3s', position: 'relative' }}
    >
      <TopoBackground />
      {isMobile && menuOpen && <NavBackdrop onClick={closeMenu} />}
      <NarrowSidebar mobileOpen={menuOpen} onNavigate={closeMenu} />
      <div
        className="vx-main-col"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', zIndex: 1, minWidth: 0 }}
      >
        {isMobile && <MobileHeader menuOpen={menuOpen} onToggle={toggleMenu} />}
        {showCatalogChrome && !isAdminPage && (
          <TopNav
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        )}
        <Lightbar />
        <main
          className="vx-main-pad"
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
          }}
        >
          {isPending ? <PendingApprovalView /> : <Outlet context={{ activeTab, searchQuery, filters } satisfies LayoutContext} />}
        </main>
        {showCatalogChrome && !isAdminPage && (
          <BottomBar
            apps={apps}
            filters={filters}
            onFiltersChange={setFilters}
            allTeams={allTeams}
          />
        )}
      </div>
    </div>
  );
}
